import React, { createContext, useContext, ReactNode, useMemo, useCallback, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchWithSchema } from '@/lib/api';
import { getSchemaFallback } from '@webready/shared/config';

interface TenantPlan {
  plan_name: 'STARTER' | 'PRO' | 'ENTERPRISE';
  status: 'active' | 'suspended' | 'cancelled';
  features: {
    enable_booking?: boolean;
    enable_staff?: boolean;
    enable_patients?: boolean;
    enable_clients?: boolean;
    // New AI features
    enable_ai_corector?: boolean;
    enable_ai_seo_optimizer?: boolean;
    enable_ai_creator_advanced?: boolean;
    // Legacy AI features (deprecated, kept for backward compatibility during transition)
    enable_ai_basic?: boolean;
    enable_ai_advanced?: boolean;
    enable_ai_pro?: boolean;
    enable_ai_image_generation?: boolean;
  };
  ai_limit_monthly: number;
  ai_limit_current: number;
  bonus_tokens?: number;
  assigned_at?: string | null;
  expires_at?: string | null;
  days_until_expiry?: number | null;
}

interface SubscriptionContextType {
  plan: TenantPlan | null;
  isLoading: boolean;
  isFeatureEnabled: (feature: string) => boolean;
  aiUsage: {
    current: number;
    limit: number;
    percentage: number;
    bonusTokens?: number;
  };
  expiresAt?: string | null;
  daysUntilExpiry?: number | null;
  assignedAt?: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  // Get schema from .env to include in query key for proper cache invalidation
  const schemaFromEnv = getSchemaFallback(import.meta.env.VITE_DB_SCHEMA);
  
  // Defer tenant-plan API call until after LCP to improve performance
  const [shouldLoad, setShouldLoad] = useState(false);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Wait for page to be interactive or use requestIdleCallback
    const loadAfterLCP = () => {
      if (document.readyState === 'complete') {
        setShouldLoad(true);
      } else {
        // Wait for LCP (typically happens within 2.5s)
        const timer = setTimeout(() => {
          setShouldLoad(true);
        }, 2500);
        
        window.addEventListener('load', () => {
          clearTimeout(timer);
          setShouldLoad(true);
        });
        
        return () => {
          clearTimeout(timer);
          window.removeEventListener('load', () => {});
        };
      }
    };
    
    // Use requestIdleCallback if available, otherwise use setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        loadAfterLCP();
      }, { timeout: 3000 });
    } else {
      loadAfterLCP();
    }
  }, []);
  
  const { data: planData, isLoading, error } = useQuery<{ success: boolean; data: TenantPlan }>({
    queryKey: ['tenant-plan', schemaFromEnv], // Include schema in query key
    queryFn: async () => {
      // console.log('[SubscriptionContext] Fetching tenant plan with schema:', schemaFromEnv);
      const response = await fetchWithSchema('/api/tenant-plan');
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[SubscriptionContext] Failed to fetch tenant plan:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Failed to fetch tenant plan: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      /* console.log('[SubscriptionContext] Tenant plan fetched successfully:', {
        success: result.success,
        planName: result.data?.plan_name,
        status: result.data?.status
      }); */
      return result;
    },
    enabled: shouldLoad, // Defer loading until after LCP
    staleTime: 1000 * 60 * 10, // 10 minutes - plan doesn't change often (increased from 5)
    gcTime: 1000 * 60 * 60, // 1 hour - keep in cache longer
    retry: 1, // Retry only once on failure (reduced from 3 for faster failure)
    retryDelay: 1000, // Fixed 1s delay (faster than exponential backoff)
    refetchOnWindowFocus: false, // Don't refetch on focus for public pages (reduces unnecessary requests)
    refetchOnMount: false, // Don't refetch on mount if deferred
  });

  const plan = planData?.data || null;
  
  // Log errors for debugging
  if (error) {
    console.error('[SubscriptionContext] Query error:', error);
  }

  // Memoize isFeatureEnabled to prevent recreation on every render
  // SIMPLIFIED: AI is enabled if ai_limit_monthly > 0 (all AI features are available)
  const isFeatureEnabled = useCallback((feature: string): boolean => {
    if (!plan || plan.status !== 'active') {
      // console.log(`[SubscriptionContext] Feature ${feature} disabled: plan=${plan ? plan.plan_name : 'null'}, status=${plan?.status}`);
      return false;
    }
    
    // SIMPLIFIED AI LOGIC: If ai_limit_monthly > 0, all AI features are enabled
    // This includes: enable_ai_basic, enable_ai_advanced, enable_ai_pro, enable_ai_corector, 
    // enable_ai_seo_optimizer, enable_ai_creator_advanced, enable_ai_image_generation
    const aiFeatures = [
      'enable_ai_basic',
      'enable_ai_advanced', 
      'enable_ai_pro',
      'enable_ai_corector',
      'enable_ai_seo_optimizer',
      'enable_ai_creator_advanced',
      'enable_ai_image_generation'
    ];
    
    if (aiFeatures.includes(feature)) {
      // AI is enabled if ai_limit_monthly > 0
      return (plan.ai_limit_monthly || 0) > 0;
    }
    
    // Direct check for non-AI features
    const enabled = plan.features[feature as keyof typeof plan.features] === true;
    return enabled;
  }, [plan]);

  // Memoize aiUsage to prevent recreation on every render
  // Include bonus_tokens in effective limit (remaining = limit - current + bonus)
  const aiUsage = useMemo(() => {
    const current = plan?.ai_limit_current || 0;
    const baseLimit = plan?.ai_limit_monthly || 0;
    const bonus = plan?.bonus_tokens ?? 0;
    const effectiveLimit = baseLimit + bonus;
    return {
      current,
      limit: effectiveLimit,
      percentage: effectiveLimit > 0 ? Math.round((current / effectiveLimit) * 100) : 0,
      bonusTokens: bonus > 0 ? bonus : undefined,
    };
  }, [plan?.ai_limit_current, plan?.ai_limit_monthly, plan?.bonus_tokens]);

  const value: SubscriptionContextType = useMemo(() => ({
    plan,
    isLoading,
    isFeatureEnabled,
    aiUsage,
    expiresAt: plan?.expires_at || null,
    daysUntilExpiry: plan?.days_until_expiry || null,
    assignedAt: plan?.assigned_at || null,
  }), [plan, isLoading, isFeatureEnabled, aiUsage]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

